/**
 * ActiveBonusesOverlay — the floating "Bonus" bubble.
 *
 * A fixed pink chip above the tab bar, lower-LEFT, shown ONLY on the Profile
 * tab ("/"): one gift icon plus "Active Bonuses", nothing else (2026-08-27
 * direction; it used to be a lower-right counter pill on every page). It
 * spent an hour at the middle of the left edge on 2026-09-19 and was in the
 * way of everything, so it is back in its slot with a TUCK: a chevron on its
 * edge slides it off screen to the left, leaving a small arrow tab to bring
 * it back, and the choice is remembered on the device (TUCK_KEY). Pink is
 * the bonus colour everywhere: this bubble and the BonusPill chips on event
 * cards share the `--bonus` token so the language is one colour, one meaning.
 * Tapping opens the Bonuses page, which carries the full breakdown and the
 * LeekDuck attribution.
 *
 * Mounted once in App.tsx (NOT AppShell, which remounts per page). Renders
 * nothing when no bonuses are live, so quiet weeks cost no chrome. The
 * lower-left slot is safe: the feedback button renders inline at the end of
 * the page, not floating (see FeedbackButton's note).
 */
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { MegaIcon } from "@/components/MegaIcon";
import { megaTimeLeft } from "@/components/MegaLevelControls";
import { activeMegaForm, useActiveMega } from "@/lib/megaLevelStore";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Gift } from "lucide-react";
import { useSession } from "./SessionProvider";
import { eventsLiveKey } from "@/lib/eventsLiveKey";
import {
  activeDailyBonusEvents,
  collectActiveBonuses,
  type BonusFeedEvent,
} from "@/lib/eventBonuses";
import { getGoPassProgress, useGoPassRanks } from "@/lib/goPassRankStore";

interface LiveEventsResponse {
  active: Array<BonusFeedEvent & { name: string }>;
}

/** Tucked off screen, remembered per device. */
const TUCK_KEY = "nexus.bonusPill.tucked.v1";
/** How much of the tucked pill stays on screen: the arrow tab. */
const TAB_PX = 26;

function readTucked(): boolean {
  try {
    return localStorage.getItem(TUCK_KEY) === "1";
  } catch {
    return false;
  }
}

export function ActiveBonusesOverlay() {
  const [loc, setLocation] = useLocation();
  const { paywallOpen, legalOpen, onboardingOpen, nexQuestionnaireOpen, trainerStepOpen } = useSession();
  useGoPassRanks(); // re-render when the trainer's saved pass rank changes

  const { data } = useQuery<LiveEventsResponse>({
    queryKey: [eventsLiveKey()],
    staleTime: 5 * 60 * 1000,
  });

  // Liveness is wall-clock: re-evaluate each minute so a bonus that ends at
  // 19:00 drops off without a reload (Spotlight Hours are exactly 1h).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Every hook sits above the route early-return below: on any other route
  // the component still renders the same hook list, or React throws #300
  // ("fewer hooks than expected") the moment the trainer leaves the Profile.
  const activeMega = useActiveMega();
  const [tucked, setTucked] = useState(readTucked);
  const toggleTucked = () => {
    setTucked((t) => {
      try {
        localStorage.setItem(TUCK_KEY, t ? "0" : "1");
      } catch {
        /* storage blocked: the choice holds for this session */
      }
      return !t;
    });
  };

  const anyModalOpen = paywallOpen || legalOpen || onboardingOpen || nexQuestionnaireOpen || trainerStepOpen;
  // Profile tab only (2026-08-27 direction; it used to float on every page).
  if (anyModalOpen || loc !== "/") return null;

  // Daily Discovery first: the recurring day-of-week bonuses (Friendship
  // Friday, Max Monday, ...) are live even when the feed has no event, and the
  // minute tick above flips them at local midnight without a reload.
  const active = collectActiveBonuses(
    [...activeDailyBonusEvents(now), ...(data?.active ?? [])],
    getGoPassProgress,
    now,
  );
  const megaForm = activeMegaForm(activeMega);
  if (active.length === 0 && !activeMega) return null;

  return (
    <div
      className="fixed left-0 z-50 flex items-center transition-transform [transition-duration:var(--duration-fast)]"
      style={{
        // The canonical "above the tab bar" offset (MyPokemon FABs, prompts).
        bottom: "calc(var(--nx-safe-bottom) + 84px)",
        // Tucked: slide left until only the arrow tab shows.
        transform: tucked ? `translateX(calc(-100% + ${TAB_PX}px))` : "translateX(12px)",
      }}
      data-testid="active-bonuses-dock"
      data-tucked={tucked ? "true" : "false"}
    >
      <button
        type="button"
        onClick={() => setLocation("/bonuses")}
        tabIndex={tucked ? -1 : 0}
        aria-hidden={tucked}
        data-testid="active-bonuses-overlay"
        aria-label={`${active.length} Pokémon GO bonuses active now${
          activeMega && megaForm ? `, ${megaForm.name} Mega Evolved` : ""
        }, open Bonuses`}
        className="inline-flex items-center gap-1.5 rounded-l-full pl-3 pr-2 py-2 text-[11px] font-bold"
        style={{
          background: "var(--surface-overlay)",
          border: "1px solid hsl(var(--bonus) / 0.5)",
          borderRight: "none",
          color: "hsl(var(--bonus))",
          boxShadow: "0 4px 18px hsl(var(--bonus) / 0.3)",
        }}
      >
        {activeMega && megaForm ? (
          <MegaIcon className="w-3.5 h-3.5" aria-hidden />
        ) : (
          <Gift className="w-3.5 h-3.5" aria-hidden />
        )}
        {activeMega && megaForm && active.length === 0
          ? `Mega ${megaTimeLeft(activeMega.expiresAt, now)}`
          : "Active Bonuses"}
      </button>
      {/* The tuck handle: slides the pill away, or brings it back. */}
      <button
        type="button"
        onClick={toggleTucked}
        aria-label={tucked ? "Show the Active Bonuses pill" : "Tuck the Active Bonuses pill away"}
        aria-expanded={!tucked}
        data-testid="active-bonuses-tuck"
        className="grid place-items-center rounded-r-full"
        style={{
          width: TAB_PX,
          height: 34,
          background: "var(--surface-overlay)",
          border: "1px solid hsl(var(--bonus) / 0.5)",
          borderLeft: "1px solid hsl(var(--bonus) / 0.25)",
          color: "hsl(var(--bonus))",
          boxShadow: "0 4px 18px hsl(var(--bonus) / 0.3)",
        }}
      >
        {tucked ? <ChevronRight className="w-3.5 h-3.5" aria-hidden /> : <ChevronLeft className="w-3.5 h-3.5" aria-hidden />}
      </button>
    </div>
  );
}
